/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Moment } from 'moment';
import * as momentTimezone from 'moment-timezone';
import { DateTimeWithTimezoneModel } from './date-time-with-timezone.model';
import { TimeModel } from './time.model';

@Injectable({
  providedIn: 'root',
})
export class DateUtilsService {
  public defaultFormat = 'YYYY-MM-DDTHH:mm:ss';
  public primengDateTimeFormat = 'YYYY-MM-DD HH:mm';

  getLocalTimezone() {
    return momentTimezone.tz.guess();
  }

  getLocalUtcOffset() {
    return moment().format('Z');
  }

  getUtcOffset(minutes: number) {
    return moment().utcOffset(minutes).format('Z');
  }

  toTimezone(
    dateTime: string,
    timezone: string | null,
    currentFormat: string | null = null,
    format: string | null = this.defaultFormat
  ) {
    let momentDate : Moment;
    if(currentFormat && timezone) {
      momentDate = momentTimezone.tz(dateTime, currentFormat, timezone);
    }

    else if(!currentFormat && timezone) {
      momentDate = momentTimezone.tz(dateTime, timezone)
    }

    else {
      momentDate = momentTimezone.tz(dateTime)
    }

    return format ? momentDate.format(format) : momentDate.format();
  }

  format(dateTime: string | Date, currentFormat?: string): string {
    return `${moment(dateTime, currentFormat).utcOffset(0).format(this.defaultFormat)}Z`;
  }

  getDateTimeAsString(
    dateTime: any,
    timezone = this.getLocalTimezone(),
    dateTimeFormat = this.primengDateTimeFormat
  ): string {
    if (!dateTime) {
      return '';
    }
    const dateTimeInSelectedTimezone = this.toTimezone(dateTime, timezone, dateTimeFormat);
    return this.format(dateTimeInSelectedTimezone);
  }

  getDateTimeWithTimezoneFromDateAndTimeStrings(date: string, time: string, timezone = this.getLocalTimezone()) {
    const dateTimeString = this.getDateTimeFromDateAndTimeStrings(date, time, timezone);
    return new DateTimeWithTimezoneModel(dateTimeString, timezone);
  }

  getDateTimeFromDateAndTimeStrings(date: string, time: string, timezone = this.getLocalTimezone()) {
    const dateTime = `${date} ${time}`;
    return this.getDateTimeAsString(dateTime, timezone);
  }

  extractTimeFromDateTimeString(dateTime: string) {
    return moment(dateTime).format('HH:mm');
  }

  extractDateFromDateTimeString(dateTime: string) {
    return moment(dateTime).format('YYYY-MM-DD');
  }

  getMomentFromDatetime(dateTimeWithTimezone: string, timezone = this.getLocalTimezone(), dateTimeFormat = this.primengDateTimeFormat) {
    const dateTimeUTCString = this.toTimezone(dateTimeWithTimezone, 'UTC', null, null);
    const dateTimeLocal = this.toTimezone(dateTimeUTCString, timezone, null, dateTimeFormat);
    return moment(dateTimeLocal);
  }

  formatToDateOnly(date: any) {
    if (date) {
      return moment(date).format('YYYY-MM-DD');
    } else {
      return null;
    }
  }

  isEqual(date1: DateTimeWithTimezoneModel, date2: DateTimeWithTimezoneModel) {
    if(!date1 || !date1.dateTime || !date1.timezone || !date2 || !date2.dateTime || !date2.timezone) {
      throw new Error('Invalid DateTimeWithTimezoneModel provided for comparison');
    }

    return this.toTimezone(date1.dateTime, date1.timezone) === this.toTimezone(date2.dateTime, date2.timezone);
  }

  getTWForGivenTime(time: string, timeFormat = 'HH:mm') {
    const dateWithGivenTime = moment(time, timeFormat);
    const dateTimeAsString = this.getDateTimeAsString(dateWithGivenTime);
    const timezone = this.getLocalTimezone();
    return new DateTimeWithTimezoneModel(dateTimeAsString, timezone);
  }

  static subtractTwoDates(date1: Date, date2: Date): number {
    return date1.getTime() - date2.getTime();
  }

  static convertStoTime(s: number): TimeModel {
    let remainingSeconds: number = s;

    const time = new TimeModel();
    time.days = Math.round(remainingSeconds / (24 * 60 * 60));
    remainingSeconds = remainingSeconds % (24 * 60 * 60);

    time.hours = Math.round(remainingSeconds / (60 * 60));
    remainingSeconds = remainingSeconds % (60 * 60);

    time.minutes = Math.round(remainingSeconds / 60);
    remainingSeconds = remainingSeconds % 60;

    time.seconds = remainingSeconds;

    return time;
  }

  static convertMsToTime(ms: number): TimeModel {
    return DateUtilsService.convertStoTime(ms / 1000);
  }

  static dateObjectWithDaysOffset = (offset: number): Date => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - offset);
    return date;
  };

  static getSixtyDaysAgo() {
    return new Date(new Date().setDate(new Date().getDate() - 60));
  }

  static getSixtyDaysAgoWithZ() {
    return new Date(new Date().setDate(new Date().getDate() - 60)).toISOString().split('.')[0] + 'Z';
  }

  static toIsoString(date: string): string {
    return new Date(date).toISOString().split('.')[0] + 'Z';
  }

  static subtractTimeOffsetFromDate(date: string, hours = 0, minutes = 0, sec = 0) {
    return moment(date)
      .subtract(hours, 'hours')
      .subtract(minutes, 'minutes')
      .subtract(sec, 'seconds')
      .format('YYYY-MM-DDTHH:mm:ss');
  }

  static subtractCurrentTimezoneOffsetFromDate(date: string) {
    return moment(date).subtract(moment().utcOffset(), 'minutes').format('YYYY-MM-DDTHH:mm:ss');
  }

  static addTimeOffsetToDate(date: string, hours = 0, minutes = 0, sec = 0) {
    return moment(date).add(hours, 'hours').add(minutes, 'minutes').add(sec, 'seconds').format('YYYY-MM-DDTHH:mm:ss');
  }

  static setTimeOffsetToDate(date: string, hours = 0, minutes = 0, sec = 0) {
    return moment(date).set({ hour: hours, minute: minutes, second: sec }).format('YYYY-MM-DDTHH:mm:ss');
  }

  /**
   * Compares two date strings and returns the difference in milliseconds.
   * If the result is negative, date1 is before date2.
   * If the result is positive, date1 is after date2.
   * If the result is zero, date1 is equal to date2.
   *
   * @param date1 - The first date string to compare.
   * @param date2 - The second date string to compare.
   * @returns The difference in milliseconds between the two dates.
   */
  static compareDates(date1: string, date2: string): number {
    return moment(date1).diff(moment(date2));
  }

  static toTimezone(date: string, timezone: string): string {
    return momentTimezone.tz(date, timezone).format('YYYY-MM-DDTHH:mm:ss');
  }

  static dateInPast(date: string): boolean {
    return moment(date).isBefore(moment());
  }

  static toLocalTimezone(date: string): string {
    const localTimezone = momentTimezone.tz.guess();
    const m = /Z|[+-]\d{2}:\d{2}$/.test(date)
      ? moment.parseZone(date)
      : moment.utc(date);

    return m.tz(localTimezone).format("YYYY-MM-DDTHH:mm:ss");
  }

  reinterpretDateTimeAtDifferentTimeZone(
    dateTimeUTC?: string,
    previousTimezone?: string,
    newTimezone?: string
  ): string {
    const localTimeInPreviousTZ = moment.utc(dateTimeUTC).tz(previousTimezone ?? this.getLocalTimezone());

    const year = localTimeInPreviousTZ.year();
    const month = localTimeInPreviousTZ.month();
    const day = localTimeInPreviousTZ.date();
    const hour = localTimeInPreviousTZ.hour();
    const minute = localTimeInPreviousTZ.minute();
    const second = localTimeInPreviousTZ.second();

    const localTimeInNewTZ = moment.tz(
      {
        year,
        month,
        day,
        hour,
        minute,
        second,
      },
      newTimezone ?? this.getLocalTimezone()
    );

    return localTimeInNewTZ.toISOString();
  }

  static toUtcTimezone(date: string): string {
    return momentTimezone.tz(date, momentTimezone.tz.guess()).utc().format('YYYY-MM-DDTHH:mm:ss');
  }
}
